import React, { createContext, useCallback, useContext, useRef } from "react";

type PracticeExitHandler = (destination: string) => void;

interface PracticeNavigationContextValue {
  registerPracticeExitHandler: (handler: PracticeExitHandler) => () => void;
  requestPracticeNavigation: (destination: string) => boolean;
}

const PracticeNavigationContext = createContext<
  PracticeNavigationContextValue | undefined
>(undefined);

export const PracticeNavigationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const exitHandlerRef = useRef<PracticeExitHandler | null>(null);

  const registerPracticeExitHandler = useCallback(
    (handler: PracticeExitHandler) => {
      exitHandlerRef.current = handler;

      return () => {
        if (exitHandlerRef.current === handler) {
          exitHandlerRef.current = null;
        }
      };
    },
    []
  );

  const requestPracticeNavigation = useCallback((destination: string) => {
    if (!exitHandlerRef.current) return false;

    exitHandlerRef.current(destination);
    return true;
  }, []);

  return (
    <PracticeNavigationContext.Provider
      value={{
        registerPracticeExitHandler,
        requestPracticeNavigation
      }}
    >
      {children}
    </PracticeNavigationContext.Provider>
  );
};

export const usePracticeNavigation = (): PracticeNavigationContextValue => {
  const context = useContext(PracticeNavigationContext);

  if (!context) {
    throw new Error(
      "usePracticeNavigation must be used inside PracticeNavigationProvider"
    );
  }

  return context;
};
